import type { RefObject } from 'react';
import type { Study, BritProtocol } from '../../api/types';
import type { MonitorChannel } from '../multi-monitor/MonitorChannel';
import type { SyncPreferences } from '../multi-monitor/types';

// ---------------------------------------------------------------------------
// Sync capability declaration
// ---------------------------------------------------------------------------

/** Which cross-window sync operations a viewer supports. */
export interface SyncCapabilitySet {
  scroll: boolean;
  windowLevel: boolean;
  tool: boolean;
  crosshair: boolean;
  annotations: boolean;
}

/** No sync support at all — used by opaque / legacy viewers. */
export const NO_SYNC_CAPABILITIES: SyncCapabilitySet = {
  scroll: false,
  windowLevel: false,
  tool: false,
  crosshair: false,
  annotations: false,
};

/** Full sync support — used by viewers we build from source. */
export const FULL_SYNC_CAPABILITIES: SyncCapabilitySet = {
  scroll: true,
  windowLevel: true,
  tool: true,
  crosshair: true,
  annotations: true,
};

// ---------------------------------------------------------------------------
// Abstract sync adapter (viewer-agnostic contract)
// ---------------------------------------------------------------------------

/**
 * Interface that every viewer-specific sync adapter must implement.
 *
 * The multi-monitor system talks to this interface — it never knows
 * whether the underlying viewer is OHIF, GWT, or a 3rd-party black box.
 *
 * Adapters translate between the viewer's native events and the
 * BRIT canonical BroadcastChannel messages.
 */
export interface ViewerSyncAdapter {
  /** Start bridging events between the viewer and the BroadcastChannel. */
  start(): void;
  /** Stop bridging and release resources. */
  stop(): void;
  /** Update which sync categories are active. */
  setPreferences(prefs: SyncPreferences): void;
}

// ---------------------------------------------------------------------------
// ViewerProvider
// ---------------------------------------------------------------------------

/**
 * A pluggable viewer backend. OHIF is the first implementation.
 * Future viewers (AI-assisted, specialty, etc.) implement this interface.
 */
export interface ViewerProvider {
  /** Unique identifier for this viewer type */
  readonly id: string;
  /** Human-readable name */
  readonly name: string;
  /** Whether this viewer supports hanging protocols */
  readonly supportsHangingProtocols: boolean;
  /** Whether this viewer supports multi-study comparison */
  readonly supportsComparisons: boolean;
  /** Which cross-window sync operations this viewer can handle */
  readonly syncCapabilities: SyncCapabilitySet;

  /**
   * Build the URL or configuration needed to launch the viewer for a study.
   * For iframe viewers, this returns the iframe src.
   */
  buildViewerConfig(params: ViewerLaunchParams): ViewerConfig;

  /**
   * Translate a Brit PACS protocol into the viewer's native format.
   * Returns null if the protocol can't be represented in this viewer.
   */
  translateProtocol?(protocol: BritProtocol): unknown;

  /**
   * Apply a protocol to an active viewer instance.
   * For iframe viewers, this uses postMessage.
   */
  applyProtocol?(viewerInstance: ViewerInstance, protocol: BritProtocol): void;

  /**
   * Capture the current viewport state as a Brit PACS protocol.
   * Used for "Save Current Layout as Protocol."
   */
  captureProtocol?(viewerInstance: ViewerInstance): Promise<BritProtocol | null>;

  /**
   * Create a sync adapter for cross-window viewport synchronization.
   * Returns null if the viewer has no sync support at all.
   *
   * Each viewer provides its own adapter that translates between its
   * native events and the BRIT canonical BroadcastChannel messages.
   */
  createSyncAdapter?(
    channel: MonitorChannel,
    windowId: string,
    iframeRef: () => HTMLIFrameElement | null,
  ): ViewerSyncAdapter | null;
}

export interface ViewerLaunchParams {
  study: Study;
  siteId: string;
  sessionId: string;
  protocol?: BritProtocol;
  comparisonStudyUids?: string[];
}

export interface ViewerConfig {
  type: 'iframe' | 'component';
  url?: string;
  component?: React.ComponentType;
  props?: Record<string, unknown>;
}

export interface ViewerInstance {
  type: 'iframe' | 'component';
  iframeRef?: RefObject<HTMLIFrameElement | null>;
  componentRef?: RefObject<unknown>;
}
